/**
 * Samples a client can ask for with any requisition. A commercial sample goes out in commercial packaging, whose
 * artwork Makin designs internally (the client never sees or sends it). Only BD updates the sample and artwork, from
 * BDS; MExP shows the tracking to RA and admins, and the sample stages to the client.
 */

export const SAMPLE_PACK_TYPES = ['plain', 'commercial'] as const;
export type SamplePackType = (typeof SAMPLE_PACK_TYPES)[number];

export const SAMPLE_PACK_LABELS: Record<SamplePackType, string> = {
  plain: 'Plain sample',
  commercial: 'Commercial sample',
};

/** In order; a sample only moves forward. */
export const SAMPLE_STATUSES = ['requested', 'in_preparation', 'dispatched', 'delivered'] as const;
export type SampleStatus = (typeof SAMPLE_STATUSES)[number];

export const SAMPLE_STATUS_LABELS: Record<SampleStatus, string> = {
  requested: 'Requested',
  in_preparation: 'In process',
  dispatched: 'Dispatched',
  delivered: 'Delivered',
};

/** Commercial samples only. In review can go back to In design for rework. */
export const ARTWORK_STATUSES = ['pending', 'in_design', 'in_review', 'approved'] as const;
export type ArtworkStatus = (typeof ARTWORK_STATUSES)[number];

export const ARTWORK_STATUS_LABELS: Record<ArtworkStatus, string> = {
  pending: 'Not started',
  in_design: 'In design',
  in_review: 'In review',
  approved: 'Approved',
};

/** Why an update is refused, or null when it is allowed. */
export function sampleTransitionProblem(
  sample: { packType: SamplePackType; status: SampleStatus; artworkStatus: ArtworkStatus | null },
  next: { status?: SampleStatus; artworkStatus?: ArtworkStatus; courierName?: string | null; trackingNo?: string | null },
): string | null {
  if (next.artworkStatus !== undefined) {
    if (sample.packType !== 'commercial') return 'Only a commercial sample has artwork';
    const from = ARTWORK_STATUSES.indexOf(sample.artworkStatus ?? 'pending');
    const to = ARTWORK_STATUSES.indexOf(next.artworkStatus);
    const rework = sample.artworkStatus === 'in_review' && next.artworkStatus === 'in_design';
    if (to < from && !rework) return 'Artwork only moves forward (or back from In review to In design)';
    if (sample.status !== 'requested' && next.artworkStatus !== 'approved') return 'The sample is already in preparation with approved artwork';
  }
  if (next.status !== undefined) {
    const from = SAMPLE_STATUSES.indexOf(sample.status);
    const to = SAMPLE_STATUSES.indexOf(next.status);
    if (to < from) return 'A sample only moves forward';
    const artwork = next.artworkStatus ?? sample.artworkStatus;
    if (sample.packType === 'commercial' && to >= SAMPLE_STATUSES.indexOf('in_preparation') && artwork !== 'approved') {
      return 'Approve the artwork before the commercial sample goes into preparation';
    }
    if (next.status === 'dispatched' && (!next.courierName?.trim() || !next.trackingNo?.trim())) return 'Give the courier and the tracking number when dispatching';
  }
  return null;
}

/** One change to a sample, as recorded for the tracking. */
export interface SampleEventDto {
  at: string;
  by: string;
  status: SampleStatus | null;
  artworkStatus: ArtworkStatus | null;
  note: string | null;
}

/** Staff view (RA, admins): the sample and, for a commercial one, its artwork. */
export interface SampleDto {
  id: string;
  requestId: string;
  packType: SamplePackType;
  status: SampleStatus;
  artworkStatus: ArtworkStatus | null;
  courierName: string | null;
  trackingNo: string | null;
  expectedDelivery: string | null;
  /** When each sample stage was reached (null until then). */
  stageDates: Record<SampleStatus, string | null>;
  artworkDates: Record<ArtworkStatus, string | null> | null;
  history: SampleEventDto[];
  createdAt: string;
  updatedAt: string;
}

/** Client view: the sample stages only; artwork and internal notes stay internal. */
export interface ClientSampleDto {
  requestId: string;
  code: string;
  productName: string;
  country: string;
  packType: SamplePackType;
  status: SampleStatus;
  stageDates: Record<SampleStatus, string | null>;
  courierName: string | null;
  trackingNo: string | null;
  expectedDelivery: string | null;
  updatedAt: string;
}
